import { useEffect, useState, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';
import Cookies from 'js-cookie';
import { MessageSquareText, Power } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTE } from '@/routes/constants';
import { THEME, THEME_ORDER, type Theme } from '@/components/login-theme';
import { useThemeAttr, setThemeAttr } from '@/hooks/use-theme-attr';
import GamificationHeader, { GamificationLeaderboard } from '@/components/gamification/GamificationHeader';
import { CompetitionRulesDialog } from '@/components/competition-rules-dialog';
// 主题轮盘走打包导入(带 hash)避免部署环境 public 绝对路径破图
import themeWheel from '@/assets/images/theme-wheel.svg';
// 侧栏品牌 logo(橙色图标 + 黑字「易學平台」/ 英文版；icon_before_logo 是白线,浅色侧栏不可见故弃用) + 机构 logo + 头像
import logoZh from '@/assets/images/logo.svg';
import logoEn from '@/assets/images/logo_en.svg';
import ugcEduhkLogo from '@/assets/practice/ugc-eduhk-logo.png';
import BoySvg from '@/assets/images/avatar-boy-svg.svg';
import GirlSvg from '@/assets/images/avatar-girl-svg.svg';
// 侧栏导航图标：未选中=灰描边(<img>)；选中=内联 SVG(currentColor 跟随 --sidebar-active)
import overviewIcon from '@/assets/nav/overview.svg';
import overviewIconActive from '@/assets/nav/overview-active.svg?raw';
import practiceIcon from '@/assets/nav/practice.svg';
import practiceIconActive from '@/assets/nav/practice-active.svg?raw';
import partnerIcon from '@/assets/nav/partner.svg';
import partnerIconActive from '@/assets/nav/partner-active.svg?raw';

// 手机横屏(844×390)学生 dashboard 共享外壳:侧栏+顶栏控制+积分卡为各页固定 chrome,主体由 children 传入;整块 FitScale 等比缩放居中,舞台内用固定 px 内联样式(勿用响应式 class,否则按真实视口求值破坏定位)

const STG_W = 844;
const STG_H = 390;

// 宽屏铺满(灌入中部):视口比 844:390 更宽时按高度缩放,横向余量作为 shift 交舞台内元素重新落位(侧栏贴左/顶栏+积分卡贴右/主体吃 shift 变宽)消留白;更窄/高时等比居中(shift=0)。同登录画布 shift 技术,各页主体经 useStageFit() 取同一 shift
export function useStageFit() {
  const [fit, setFit] = useState({ scale: 1, shift: 0 });
  useEffect(() => {
    const update = () => {
      const sW = window.innerWidth / STG_W;
      const sH = window.innerHeight / STG_H;
      if (sW >= sH) setFit({ scale: sH, shift: window.innerWidth / sH - STG_W });
      else setFit({ scale: sW, shift: 0 });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return fit;
}

interface Props {
  children: ReactNode;
}

export default function MobileLandscapeDashboardShell({ children }: Props) {
  const { t, i18n } = useTranslation();
  const { getCurrentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = getCurrentUser();
  const theme = useThemeAttr();

  // 宽屏铺满:按高度缩放并把横向余量 shift 灌入舞台(见 useStageFit)。侧栏贴左 / 顶栏+积分卡贴右 / 页面主体吃 shift。
  const { scale, shift } = useStageFit();

  // 语言切换(与登录横屏一致)
  const current = Cookies.get('i18next') || i18n.language || 'zh-HK';
  const isEn = current.startsWith('en');
  const setLang = (lng: 'en' | 'zh-HK') => { i18n.changeLanguage(lng); Cookies.set('i18next', lng); };

  // Sentry 意见反馈表单(仅生产初始化;开发无操作) —— 与 app-sidebar 一致
  const openFeedback = async () => {
    try {
      const feedback = Sentry.getFeedback?.();
      if (!feedback) return;
      const form = await feedback.createForm();
      form.appendToDom();
      form.open();
    } catch (e) {
      console.warn('Feedback widget unavailable:', e);
    }
  };

  // 英文「Leaderboard」实测墨迹 73.6px > 胶囊 67 会溢出(中文「排行榜」24px 富余),故英文加宽胶囊
  // 并让它与左邻的「規則」同步左移同样距离——右侧语言/登出保持 Figma 原位,左侧到主题切换器仍有 30px 余量
  const boardW = isEn ? 88 : 67;
  const capsuleShift = boardW - 67;

  // 主题驱动的胶囊配色：橙色主题用设计稿原色,其余主题跟随 var(--accent)(与桌面 chrome 同套路)
  const rulesBg = theme === 'orange' ? '#8FCEE0' : 'var(--accent)';
  const boardBg = theme === 'orange' ? '#FFD6DC' : 'var(--accent)';
  const langBg = theme === 'orange' ? '#FFE7D0' : 'var(--accent)';

  // 主题切换器色块(与 ThemeSelector 一致：橙=四色轮盘,其余=纯色圆点)
  const swatch = (th: Theme, size: number) =>
    th === 'orange'
      ? <img src={themeWheel} alt="" style={{ width: size, height: size, display: 'block' }} />
      : <span style={{ width: size, height: size, borderRadius: '50%', background: THEME[th].dot, display: 'block' }} />;
  const others = THEME_ORDER.filter((x) => x !== theme);

  const NAV = [
    { url: ROUTE.STUDENT.OVERVIEW, title: t('student.overview'), icon: overviewIcon, active: overviewIconActive, top: 140, related: [] as string[] },
    { url: ROUTE.STUDENT.PRACTICE_HOME, title: t('student.practice'), icon: practiceIcon, active: practiceIconActive, top: 183, related: [ROUTE.STUDENT.PRACTICE_EDIT] },
    { url: ROUTE.STUDENT.SELF_ASSESS, title: t('student.aibot'), icon: partnerIcon, active: partnerIconActive, top: 229, related: [] as string[] },
  ];

  const avatarSrc = user?.sex === 'female' ? GirlSvg : user?.sex === 'X' ? null : BoySvg;

  return (
    <div
      style={{ width: STG_W + shift, height: STG_H, transform: `scale(${scale})`, transformOrigin: 'center', position: 'relative', flex: '0 0 auto' }}
    >
      {/* ─── 侧边栏 ─── */}
      <div style={{ position: 'absolute', left: 11, top: 12, width: 182, height: 366, borderRadius: 20, background: '#F3F3F4' }}>
        {/* 品牌 logo(橙色图标 + 易學平台/英文版)：frame 组 [63,26 79x24] → 容器内 rel(52,14) */}
        <img src={isEn ? logoEn : logoZh} alt="易學平台" draggable={false} style={{ position: 'absolute', left: 52, top: 14, height: 24, width: 'auto', display: 'block' }} />
        {/* 头像(圆形) frame [84,65] → rel(73,53) */}
        <div style={{ position: 'absolute', left: 73, top: 53, width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', background: '#000' }}>
          {avatarSrc && <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
        </div>
        {/* 姓名 / 邮箱(整栏居中) frame 107/125 → rel 95/113 */}
        <div style={{ position: 'absolute', left: 0, top: 95, width: 182, textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#000', fontFamily: 'var(--font-sans)' }}>
          {user?.name}
        </div>
        <div style={{ position: 'absolute', left: 0, top: 113, width: 182, textAlign: 'center', fontSize: 11, color: 'rgba(115,119,123,0.7)', fontFamily: 'var(--font-sans)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 8px' }}>
          {user?.email}
        </div>

        {/* 导航(3 项) */}
        {NAV.map((n) => {
          const isActive = pathname === n.url || n.related.includes(pathname);
          return (
            <button
              key={n.url}
              type="button"
              onClick={() => navigate(n.url)}
              style={{
                position: 'absolute', left: 16, top: n.top, width: 153, height: 36, borderRadius: 11,
                background: isActive ? '#FFFFFF' : 'transparent',
                boxShadow: isActive ? '0 2px 6px -3px rgba(0,0,0,0.25)' : 'none',
                border: 0, cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', gap: 11, paddingLeft: 15,
                color: isActive ? 'var(--sidebar-active)' : '#64748B',
              }}
              className={isActive ? '' : 'hover:bg-white/60'}
            >
              {isActive
                ? <span aria-hidden style={{ width: 15, height: 15, display: 'inline-flex', flexShrink: 0 }} className="[&>svg]:size-full" dangerouslySetInnerHTML={{ __html: n.active }} />
                : <img src={n.icon} alt="" style={{ width: 15, height: 15, flexShrink: 0, display: 'block' }} />}
              <span style={{ fontSize: 12, fontWeight: isActive ? 500 : 400, fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap' }}>{n.title}</span>
            </button>
          );
        })}

        {/* 机构 logo(UGC + 教大)：整栏居中(144 宽 → left 19),置于 Feedback 上方 */}
        <img src={ugcEduhkLogo} alt="" style={{ position: 'absolute', left: 19, top: 294, width: 144, height: 'auto', display: 'block' }} />

        {/* Feedback 按钮 */}
        <button
          type="button"
          onClick={openFeedback}
          style={{
            position: 'absolute', left: 16, top: 318, width: 153, height: 36, borderRadius: 11,
            background: 'var(--practice-feedback-bg)', color: 'var(--practice-feedback-fg)',
            border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 11, paddingLeft: 15,
          }}
          className="hover:brightness-95"
        >
          <MessageSquareText size={13} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap' }}>{t('common.feedback')}</span>
        </button>
      </div>

      {/* ─── 顶栏控制行(y=12 h=24) ─── */}
      {/* 主题切换器（MOB-02：色点视觉 15px，按钮触控区扩大到约 44×44px） */}
      {/* 主题切换器左移，避免 4 个 44×44 触控区与右侧「規則」按钮重叠 */}
      <div role="group" aria-label={t('common.themeLabel', { defaultValue: '主題色' })} style={{ position: 'absolute', left: 236 + shift, top: 12, height: 24, display: 'flex', alignItems: 'center', gap: 7, padding: '0 7px', borderRadius: 12, background: '#FFFFFF', overflow: 'visible' }}>
        {/* MOB-02：当前主题色块必须带 role。裸 <span> 隐式 role=generic，ARIA 规范禁止在 generic 上用
            aria-label（Gecko 直接丢弃、NVDA/JAWS 不播报），读屏用户听不到「當前主題：橙色」。
            改用 role="img"——「一个有可访问名称的图形」，与色块语义精确吻合且 img 角色明确支持 aria-label。
            不做成 <button disabled>：该元素本就不可点击，且 disabled 控件在读屏的表单浏览模式下会被跳过。
            内层 swatch() 的 <img alt=""> 保持空 alt（装饰性，名称由外层提供，否则会读两遍）。 */}
        <span role="img" style={{ lineHeight: 0, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} aria-label={t('common.theme.current', { defaultValue: '當前主題：{{theme}}', theme: t(`common.theme.${theme}`, { defaultValue: theme }) })}>
          {/* 非颜色选中标记：与桌面 ThemeSelector 同款环形描边(#64748B)，色盲用户不靠颜色也能分辨「当前」。
              环走 box-shadow 不占布局，套在 15px 色块外(不是 44px 热区外)，胶囊尺寸/坐标零变化。 */}
          <span className="inline-flex rounded-full ring-2 ring-[#64748B] ring-offset-1 ring-offset-white" style={{ lineHeight: 0 }}>{swatch(theme, 15)}</span>
        </span>
        <span style={{ width: 1, height: 15, background: '#F3F3F4' }} />
        {others.map((th) => (
          <button key={th} type="button" onClick={() => setThemeAttr(th)} aria-label={t('common.theme.switchTo', { defaultValue: '切換到{{theme}}主題', theme: t(`common.theme.${th}`, { defaultValue: th }) })} className="transition-transform hover:scale-110" style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {swatch(th, 15)}
          </button>
        ))}
      </div>

      {/* 規則(打开競賽規則 PDF 弹窗) — MOB-03：高度 24→32 增大触控区 */}
      {/* EN-01：原内联弹窗是最后一个没吃到修复的入口(裸 iframe 无失败提示,且整段被 rulesPdfUrl && 包着,资源缺失点「規則」什么都不弹)，改为复用 competition-rules-dialog；该组件内部按 max-height:500px 自取 292×374 与 showTitle=false，844×390 下外观零变化 */}
      <CompetitionRulesDialog>
        <button type="button" style={{ position: 'absolute', left: 499 - capsuleShift + shift, top: 8, width: 67, height: 32, borderRadius: 12, background: rulesBg, border: 0, cursor: 'pointer', fontSize: 12, color: '#000', fontFamily: 'var(--font-sans)' }} className="hover:brightness-[0.97] after:absolute after:left-0 after:right-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-['']">
          {t('common.rules', '規則')}
        </button>
      </CompetitionRulesDialog>

      {/* 排行榜(复用 GamificationLeaderboard 的弹窗+数据,自定义胶囊触发器) */}
      <GamificationLeaderboard>
        <button type="button" style={{ position: 'absolute', left: 575 - capsuleShift + shift, top: 8, width: boardW, height: 32, borderRadius: 12, background: boardBg, border: 0, cursor: 'pointer', fontSize: 12, color: '#000', fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap' }} className="hover:brightness-[0.97] after:absolute after:left-0 after:right-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-['']">
            {t('common.leaderboard')}
          </button>
      </GamificationLeaderboard>

      {/* 语言切换 — MOB-03：高度 24→32 增大触控区 */}
      <div style={{ position: 'absolute', left: 651 + shift, top: 8, width: 109, height: 32, display: 'flex', alignItems: 'center', gap: 4, padding: 2, borderRadius: 12, background: langBg }}>
        <button type="button" onClick={() => setLang('en')} className="relative after:absolute after:left-0 after:right-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-['']" style={{ flex: '1 1 0', height: 20, borderRadius: 10, background: isEn ? '#FFF' : 'transparent', color: '#000', fontSize: 12, fontWeight: isEn ? 500 : 400, border: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>English</button>
        <button type="button" onClick={() => setLang('zh-HK')} className="relative after:absolute after:left-0 after:right-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-['']" style={{ flex: '1 1 0', height: 20, borderRadius: 10, background: !isEn ? '#FFF' : 'transparent', color: '#000', fontSize: 12, fontWeight: !isEn ? 500 : 400, border: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>繁體</button>
      </div>

      {/* 登出 */}
      <button type="button" onClick={logout} style={{ position: 'absolute', left: 769 + shift, top: 12, width: 64, height: 24, borderRadius: 12, background: '#F3F3F4', border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, color: '#8F8F8F', fontSize: 12, fontFamily: 'var(--font-sans)' }} className="hover:brightness-95 after:absolute after:left-0 after:right-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-['']">
        <Power size={12} />
        {t('common.logout')}
      </button>

      {/* 右上积分卡(复用 GamificationHeader，含 MPL/KP/GS)：卡已原生 216×43(Figma 569-930)，右上角锚定 rel(617,46)，不再二次缩放 */}
      <div style={{ position: 'absolute', top: 46, right: 11 }}>
        <GamificationHeader />
      </div>

      {/* ─── 页面主体(标题 + 面板)：各页在舞台坐标系内 absolute 定位 ─── */}
      {children}
    </div>
  );
}
